import {
Observable,
empty as observableEmpty,
zip as observableZip,
} from 'rxjs';

import {
Component,
Inject,
Input,
ViewContainerRef
} from '@angular/core';
import {
FormBuilder,
FormControl,
FormGroup,
FormsModule,
Validators,
} from '@angular/forms';
import { MatDialog,MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute,Router,RouterModule } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { mergeMap } from 'rxjs/operators';

import { ConfirmDialog } from '../../dialogs/confirm.dialog';
import {
AuthenticationService,
AuthenticationServiceToken,
LogServiceToken,
MapperServiceToken,
RepositoryServiceToken,
} from '../../services';
import {
ILogService,
IMapperService,
IRepositoryService
} from '../../services/i';
import {
DocumentViewModel,
ReviewStatuses,
ReviewViewModel,
UserViewModel,
CorrespondenceViewModel as VMCorr
} from '../../view-models';
import {
BaseFilterViewModel as CorFilter
} from '../../view-models/filters';
import { IFilterViewModel } from '../../view-models/filters/i';

import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { HeaderComponent } from '../header/header.component';
@Component({
  selector: 'app-add-review',
  templateUrl: './add-review.component.html',
  styleUrls: ['./add-review.component.css'],
  imports: [
    HeaderComponent,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatSelectModule,
    RouterModule,
    MatInputModule,
    MatRadioModule
  ],
  standalone: true,
})
export class AddReviewComponent {
  editMode = false;
  showErrors = false;
  submitAttempted = false;
  filter: IFilterViewModel = new CorFilter();
  corrObs: Observable<VMCorr>;
  reviewerObs: Observable<UserViewModel>;
  documents: DocumentViewModel[] = [];
  statuses: { key: string; value: string }[] = ReviewStatuses;
  returnRoute: string;
  userSelected = false;

  @Input() keywords: FormControl = new FormControl();
  @Input() myForm: FormGroup;

  dialogRef: MatDialogRef<ConfirmDialog>;

  // http://blog.angular-university.io/introduction-to-angular-2-forms-template-driven-vs-model-driven/
  // Regarding subscribe and emit: https://scotch.io/tutorials/angular-2-http-requests-with-observables
  // Automapper (if really necessary): https://github.com/loedeman/AutoMapper/wiki
  constructor(
    @Inject(RepositoryServiceToken) private repo: IRepositoryService,
    @Inject(MapperServiceToken) private mapper: IMapperService,
    @Inject(AuthenticationServiceToken) public auth: AuthenticationService,
    @Inject(LogServiceToken) private log: ILogService,
    public dialog: MatDialog,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    private vcr: ViewContainerRef
  ) {
    this.myForm = this.buildForm();

    // this.myForm.disable();

    this.returnRoute = route.snapshot.url.reduce(
      (p, s) => `${p}/${s.path}`,
      ''
    );
    this.corrObs = this.route.params.pipe(
      mergeMap(params => {
        const id: number = +params['id'];
        return this.repo.Get(VMCorr, id);
      })
    );

    this.reviewerObs = this.route.queryParams.pipe(
      mergeMap(qparams => {
        const id: number = +qparams['userId'];
        if (id > 0) {
          this.userSelected = true;
          return this.repo.Get(UserViewModel, id);
        } else {
          return observableEmpty();
        }
      })
    );
  }

  buildForm(): FormGroup {
    return this.fb.group({
      review: [''],
      primary: ['', Validators.required],
      instructions: [''],
      status: ['assigned', Validators.required],
    });
  }

  formValid() {
    return this.myForm.valid && this.userSelected;
  }

  openDialog(message: string): Promise<any> {
    this.dialogRef = this.dialog.open(ConfirmDialog, {
      disableClose: false,
    });
    this.dialogRef.componentInstance.title = 'Confirm';
    this.dialogRef.componentInstance.message = message;
    this.dialogRef.componentInstance.actionBtn = 'Yes';
    this.dialogRef.componentInstance.cancelBtn = 'No';

    this.dialogRef.afterClosed().subscribe(result => {
      this.dialogRef = null;
    });
    return this.dialogRef.afterClosed().toPromise();
  }

  goToCorrespondence() {
    this.corrObs.subscribe(corr => {
      const text = this.myForm.get('review').value;
      if (text && text.length) {
        this.openDialog(
          'You seem to have unsaved changes. Are you sure you want to cancel?'
        ).then(yes => {
          if (yes) {
            this.router.navigate(['/correspondence', corr.Id]);
          }
        });
      } else {
        this.router.navigate(['/correspondence', corr.Id]);
      }
    });
  }

  addReview() {
    this.log.debug(this.corrObs, this.reviewerObs);
    observableZip(this.corrObs, this.reviewerObs).subscribe(values => {
      const corr = values[0];
      const reviewer = values[1];
      this.log.debug(reviewer, corr);
      if (this.myForm.errors || !reviewer) {
        this.showErrors = true;
        return;
      }

      const user = this.auth.getUserInfo<UserViewModel>();
      if (user) {
        const rev = new ReviewViewModel();
        rev.Primary = this.myForm.get('primary').value;
        rev.ReviewText = this.myForm.get('review').value;
        rev.ReviewStatus = this.myForm.get('status').value;
        rev.Instructions = this.myForm.get('instructions').value;
        rev.UserId = reviewer.Id;
        rev.CorrespondenceId = corr.Id;
        this.repo
          .Create(ReviewViewModel, rev)
          .toPromise()
          .then(newRev => {
            if (rev.Primary) {
              corr.Status = 'Assigned';
              this.repo
                .Update(VMCorr, corr)
                .toPromise()
                .then(() => {
                  this.success(newRev, corr);
                });
            } else {
              this.success(newRev, corr);
            }
          });
      } else {
        this.toastr.error('Your session has expired. Please login, again.');
      }
    });
  }

  success(rev: ReviewViewModel, corr: VMCorr) {
    const rt = rev.ReviewText ? rev.ReviewText.trim() : '';
    const ct = corr.Summary ? corr.Summary.trim() : '';
    const message = [
      'Review ',
      rt.length ? `"${rt.substr(0, 20)}${rt.length > 20 ? '...' : ''}" ` : '',
      'has been created',
      ct.length
        ? ` for "${ct.substr(0, 20)}${ct.length > 20 ? '...' : ''}".`
        : '.',
    ].join('');
    this.toastr.success(message);
    this.router.navigate(['/correspondence', corr.Id, 'reviews', rev.Id]);
  }
}
